import React, { useEffect, useRef, useState } from 'react'
import * as d3 from 'd3'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { IFitnessIndex, TRUNCATED_KEY_PARAM } from '../../shared/types/Stabledata.type'
import { TinsightParam } from '../../shared/types/computedKeyParam.type'
import { Box, Card, CardContent, Typography } from '@mui/material'
import palette from '../../theme/palette'
import { t } from 'i18next'
import { getRandomColor } from '../../shared/utils/imageUtils'
import { get, isEmpty } from 'lodash'

dayjs.extend(duration)

/* ✅ TYPES */
interface HorsePoint extends d3.SimulationNodeDatum {
  name: string
  x: number
  y: number
  r: number
  color: string

  // for labels
  xOriginal?: number
  yOriginal?: number
  labelX?: number
  labelY?: number
}

function FitnessBubbleD3({
  data,
  insightParams
}: {
  data: IFitnessIndex
  insightParams: TinsightParam[]
}) {
  const svgRef = useRef<SVGSVGElement | null>(null)

  const [TIME_TO_100_BPM_KEY, setTime] = useState<TRUNCATED_KEY_PARAM | null>(null)
  const [BEST_600_KEY, setBest] = useState<TRUNCATED_KEY_PARAM | null>(null)

  const BLUE = 'rgba(3, 138, 255,1)'
  const RED = 'rgba(255, 99, 71,1)'
  const GREEN = 'rgba(80, 200, 120,1)'
  const YELLOW = 'rgba(253, 218, 13, 1)'

  const width = 900
  const height = 650
  const margin = { top: 50, right: 50, bottom: 50, left: 70 }

  /* ✅ Get keys */
  useEffect(() => {
    if (!isEmpty(data.horse_averages)) {
      const element = data.horse_averages.at(0)?.averages || []

      if (element.length > 1) {
        setTime(get(element, '[0]', null))
        setBest(get(element, '[1]', null))
      }
    }
  }, [data.horse_averages])

  const formatValueBasedOnKey = (value: number, unit?: string, fixDecimal = false) => {
    switch (unit) {
      case "s":
        return dayjs.duration(Number(value), 'seconds').format('mm:ss')
      case "%":
        return (fixDecimal ? value.toFixed(2).toString() : value.toString()).concat('%')
      default:
        return fixDecimal ? value.toFixed(2).toString() : value.toString()
    }
  }


  const xAxisInsightParam = insightParams.find(e=>e?.name === BEST_600_KEY?.parameter_name)
  const yAxisInsightParam = insightParams.find(e=>e?.name === TIME_TO_100_BPM_KEY?.parameter_name)

  const horseData: HorsePoint[] = data.horse_averages.map((avg: any) => ({
    name: avg.horse.name,
    x:
      avg.averages.find((e: any) => e.parameter_name === BEST_600_KEY?.parameter_name)
        ?.raw_numeric_value || 0,
    y:
      avg.averages.find((e: any) => e.parameter_name === TIME_TO_100_BPM_KEY?.parameter_name)
        ?.raw_numeric_value || 0,
    // ✅ Smaller bubbles
    r: 3 + Math.random() * 1.5,
    color: getRandomColor()
  }))



  const medX: number[] = data.averages
    .filter(
      (e: any) =>
        e.key_parameter_layout?.insight_parameter?.name === BEST_600_KEY?.parameter_name
    )
    .map((e: any) => e.raw_numeric_value)

  const medY: number[] = data.averages
    .filter(
      (e: any) =>
        e.key_parameter_layout?.insight_parameter?.name ===
        TIME_TO_100_BPM_KEY?.parameter_name
    )
    .map((e: any) => e.raw_numeric_value)

  const getTranslatedParamByKey = (key?: string) => {

    if (!key) return ""

    const found = insightParams.find((param) => param.name === key)

    return found?.name_translated ?? ""
  }

  /* ✅ DRAW */
  useEffect(() => {
    if (!svgRef.current || isEmpty(horseData)) return

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    // 1. Calculate Chart Dimensions
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    /* 2. Definitions: ClipPath & Gradients */
    const defs = svg.append('defs')

    // This clip path ensures nothing leaks into the margins/axis area
    defs.append('clipPath')
      .attr('id', 'chart-area-clip')
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', chartWidth)
      .attr('height', chartHeight)

    const createGradient = (id: string, color: string, cx: string, cy: string) => {
      const grad = defs.append('radialGradient')
        .attr('id', id)
        .attr('cx', cx)
        .attr('cy', cy)
        .attr('r', '30%') // Larger radius ensures the glow fills the quadrant

      grad.append('stop').attr('offset', '0%').attr('stop-color', color).attr('stop-opacity', .62)
      grad.append('stop').attr('offset', '100%').attr('stop-color', color).attr('stop-opacity', 0)
    }

    createGradient('gradTL', BLUE, '56%', '52%')     // Top Left
    createGradient('gradTR', RED, '44%', '52%')    // Top Right
    createGradient('gradBL', GREEN, '56%', '48%')  // Bottom Left
    createGradient('gradBR', YELLOW, '44%', '48%') // Bottom Right

    /* 3. Render Background (Inner Chart Only) */
    const bg = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)
      .attr('clip-path', 'url(#chart-area-clip)')

    // Centers are placed at the corners of the inner chart area
    bg.append('circle').attr('cx', 0).attr('cy', 0).attr('r', 600).attr('fill', 'url(#gradTL)')
    bg.append('circle').attr('cx', chartWidth).attr('cy', 0).attr('r', 600).attr('fill', 'url(#gradTR)')
    bg.append('circle').attr('cx', 0).attr('cy', chartHeight).attr('r', 600).attr('fill', 'url(#gradBL)')
    bg.append('circle').attr('cx', chartWidth).attr('cy', chartHeight).attr('r', 600).attr('fill', 'url(#gradBR)')

    /* 4. Scales & Axes */
    const extentX = d3.extent(horseData, d => d.x) as [number, number];
    const extentY = d3.extent(horseData, d => d.y) as [number, number];

    const allXValues = [...extentX, ...medX].filter((d): d is number => d !== undefined);
    const allYValues = [...extentY, ...medY].filter((d): d is number => d !== undefined);

    const domainX: [number, number] = [d3.min(allXValues) ?? 0, d3.max(allXValues) ?? 1];
    const domainY: [number, number] = [d3.min(allYValues) ?? 0, d3.max(allYValues) ?? 1];

    const xScale = d3.scaleLinear().domain(domainX).nice().range([margin.left, width - margin.right])
    const yScale = d3.scaleLinear().domain(domainY).nice().range([height - margin.bottom, margin.top])
    const jitter = () => (Math.random() - 0.5) * 4

    // Render Axes
    svg.append('g')
      .attr('transform', `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(xScale).tickFormat((d: any) => formatValueBasedOnKey(Number(d), xAxisInsightParam?.unit)))
      

    svg.append('g')
      .attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(yScale).tickFormat((d: any) => formatValueBasedOnKey(Number(d), yAxisInsightParam?.unit)))

    /* 5. Axis titles */
    svg.append('text').attr('x', width / 2).attr('y', height - 5)
      .attr('text-anchor', 'middle').style('fill', '#666')
      .style('font-size', '12px')
      .text(getTranslatedParamByKey(BEST_600_KEY?.parameter_name) ?? '')

    svg.append('text').attr('transform', 'rotate(-90)').attr('x', -height / 2).attr('y', 15)
      .attr('text-anchor', 'middle').style('fill', '#666')
      .style('font-size', '12px')
      .text(getTranslatedParamByKey(TIME_TO_100_BPM_KEY?.parameter_name) ?? '')

    /* 6. Median lines */
    svg.selectAll('.medX').data(medX).enter()
      .append('line')
      .attr('x1', d => xScale(d))
      .attr('x2', d => xScale(d))
      .attr('y1', margin.top)
      .attr('y2', height - margin.bottom)
      .attr('stroke', BLUE)
      .attr('stroke-dasharray', '5,5')
      .attr('stroke-width', 2)

    svg.selectAll('.medY').data(medY).enter()
      .append('line')
      .attr('y1', d => yScale(d))
      .attr('y2', d => yScale(d))
      .attr('x1', margin.left)
      .attr('x2', width - margin.right)
      .attr('stroke', palette.light.primary.main)
      .attr('stroke-dasharray', '5,5')
      .attr('stroke-width', 2)

    /* 7. Bubbles */
    const points = svg.append('g')
    points.selectAll('circle').data(horseData).enter().append('circle')
      .attr('cx', d => xScale(d.x) + jitter())
      .attr('cy', d => yScale(d.y) + jitter())
      .attr('r', d => d.r)
      .attr('fill', d => d.color)
      .attr('stroke', 'white')
      .attr('stroke-width', 1)
      .attr('opacity', 0.85)

    /* 8. Labels & Force Simulation */
    horseData.forEach(d => {
      d.xOriginal = xScale(d.x)
      d.yOriginal = yScale(d.y)
      d.labelX = d.xOriginal! + d.r + 2
      d.labelY = d.yOriginal!
    })

    const labelGroup = svg.append('g').attr('class', 'labels')
    const labels = labelGroup.selectAll('text').data(horseData).enter()
      .append('text')
      .style('font-size', '8px')
      .style('fill', '#000')
      .attr('text-anchor', 'start')
      .text(d => d.name)
      .attr('x', d => d.labelX!)
      .attr('y', d => d.labelY!)

    const links = svg.append('g').attr('class', 'label-links').selectAll('line').data(horseData).enter()
      .append('line')
      .attr('stroke', 'gray')
      .attr('stroke-width', 0.5)

    labels.each(function (d: any) {
      const box = (this as SVGTextElement).getBBox();
      // We store the width to use it for the right-side boundary clamp
      d.width = box.width;
      d.height = box.height;
      // collision radius should be based on the largest dimension
      d.r = Math.max(box.width, box.height) / 2 + 2;
    });

    const simulation = d3.forceSimulation(horseData)
      .force('x', d3.forceX((d: any) => d.labelX!).strength(0.2))
      .force('y', d3.forceY((d: any) => d.labelY!).strength(0.2))
      .force('collide', d3.forceCollide((d: any) => d.r).iterations(4))
      .stop()

    for (let i = 0; i < 200; i++) simulation.tick()

    const MAX_OFFSET = 12
    horseData.forEach((d: any) => {
      const textWidth = d.width || 0;
      const textHeight = d.height || 0;

      // CLAMP X: Stay between left margin and right margin
      // Since text-anchor is 'start', d.x is the LEFT edge of the text
      if (d.x < margin.left) d.x = margin.left;
      if (d.x + textWidth > width - margin.right) {
        d.x = width - margin.right - textWidth;
      }

      // CLAMP Y: Stay between top and bottom margins
      if (d.y < margin.top + textHeight) d.y = margin.top + textHeight;
      if (d.y > height - margin.bottom) d.y = height - margin.bottom;
    });


    labels.attr('x', d => d.x!).attr('y', d => d.y!)
    links
      .attr('x1', d => d.xOriginal!)
      .attr('y1', d => d.yOriginal!)
      .attr('x2', d => d.x!)
      .attr('y2', d => d.y!)
      .attr('display', d => {
        const dx = Math.abs(d.x! - d.xOriginal!)
        const dy = Math.abs(d.y! - d.yOriginal!)
        return dx > 3 || dy > 3 ? 'block' : 'none'
      })

    /* 9. Median Labels */
    svg.selectAll('.medianLabelY').data(medY).enter()
      .append('text')
      .attr('x', margin.left + 5)
      .attr('y', d => yScale(d) + 10)
      .attr('text-anchor', 'start')
      .style('font-weight', 'bold')
      .style('fill', palette.light.primary.main)
      .style('font-size', '9px')
      .text(d => formatValueBasedOnKey(Number(d), yAxisInsightParam?.unit, true));

    svg.selectAll('.medianLabelX').data(medX).enter()
      .append('text')
      .attr('x', d => xScale(d) + 8)
      .attr('y', height - margin.bottom - 5)
      .attr('text-anchor', 'start')
      .style('font-weight', 'bold')
      .style('fill', BLUE)
      .style('font-size', '9px')
      .text(d => formatValueBasedOnKey(Number(d), xAxisInsightParam?.unit, true));

  }, [horseData, BEST_600_KEY, TIME_TO_100_BPM_KEY])


  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ color: palette.light.primary.main }}>
          {t('stable:title:'.concat(data.type))}
        </Typography>
        <img
          style={{ width: 55, height: 55, marginRight: 15 }}
          src="/arioneo_logo_white.png"
          alt="logo"
        />
      </Box>

      <Typography variant="caption" color="#808080">
        {t('stable:fitnessExplication')}
      </Typography>

      <div style={{ width: '1200px', display: 'flex', marginTop: 10 }}>
        <Box sx={{ width: '900px' }}>
          <svg
            ref={svgRef}
            width={width}
            height={height}
            style={{ backgroundColor: 'transparent' }}
          />
        </Box>

        <Card
          sx={{ minHeight: '80%', alignSelf: 'center', width: '300px', bgcolor: '#eee' }}
          elevation={4}
        >
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Typography variant="subtitle2" alignSelf="center">
              {t('stable:key:dbTitle')}
            </Typography>
            <Typography color={'green'} variant="h6">
              {t('stable:key:fhfb')}
            </Typography>
            <Typography color={'blue'} variant="h6">
              {t('stable:key:fhfi')}
            </Typography>
            <Typography color={'orange'} variant="h6">
              {t('stable:key:shfb')}
            </Typography>
            <Typography color={'red'} variant="h6">
              {t('stable:key:shfi')}
            </Typography>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

export default FitnessBubbleD3